import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { MessageCircle, ExternalLink } from "lucide-react";
import { cn } from "@/lib/utils";

const PERIODS = [
  { key: "today", label: "Oggi" },
  { key: "7d", label: "7 giorni" },
  { key: "30d", label: "30 giorni" },
  { key: "all", label: "Tutto" },
];

function periodFrom(key) {
  const now = new Date();
  if (key === "today") { const d = new Date(now); d.setHours(0, 0, 0, 0); return d.toISOString(); }
  if (key === "7d") return new Date(now.getTime() - 7 * 864e5).toISOString();
  if (key === "30d") return new Date(now.getTime() - 30 * 864e5).toISOString();
  return null;
}

function fmt(iso) {
  try { return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit", timeZone: "Europe/Rome" }); }
  catch { return ""; }
}

export default function WhatsAppPage() {
  const { isAdmin } = useAuth();
  const [period, setPeriod] = useState("30d");
  const [userId, setUserId] = useState("");
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState({ counts: { prepared: 0, opened: 0, confirmed: 0, cancelled: 0 }, total: 0, by_user: [] });
  const [messages, setMessages] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isAdmin) api.get("/users").then((r) => setUsers(r.data.filter((u) => u.role === "commerciale"))).catch(() => {});
  }, [isAdmin]);

  const load = useCallback(() => {
    setLoading(true);
    const date_from = periodFrom(period);
    const params = {};
    if (date_from) params.date_from = date_from;
    if (isAdmin && userId) params.user_id = userId;
    const msgParams = { ...params };
    if (statusFilter) msgParams.status = statusFilter;
    Promise.all([
      api.get("/whatsapp/stats", { params }),
      api.get("/whatsapp/messages", { params: msgParams }),
    ]).then(([s, m]) => { setStats(s.data); setMessages(m.data); }).catch(() => {}).finally(() => setLoading(false));
  }, [period, userId, isAdmin, statusFilter]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-5 animate-fade-up">
      <div className="flex items-center gap-2">
        <MessageCircle className="w-6 h-6 text-emerald-500" />
        <h1 className="font-heading text-2xl md:text-3xl font-black tracking-tight">WhatsApp</h1>
      </div>

      {/* Filtri */}
      <div className="flex flex-wrap items-center gap-2">
        {PERIODS.map((p) => (
          <button key={p.key} data-testid={`wa-period-${p.key}`} onClick={() => setPeriod(p.key)}
            className={cn("px-3 py-1.5 rounded-md text-sm border transition-colors", period === p.key ? "bg-emerald-500 text-white border-emerald-500" : "hover:bg-accent")}>
            {p.label}
          </button>
        ))}
        {isAdmin && (
          <select data-testid="wa-user-filter" value={userId} onChange={(e) => setUserId(e.target.value)}
            className="ml-auto h-9 rounded-md border bg-background px-3 text-sm">
            <option value="">Tutti i commerciali</option>
            {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
        )}
      </div>

      {/* Contatori per stato (cliccabili) */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { key: "", label: "Tutti", val: (stats.counts?.prepared || 0) + (stats.counts?.opened || 0) + (stats.counts?.confirmed || 0) + (stats.counts?.cancelled || 0), cls: "" },
          { key: "confirmed", label: "Confermati", val: stats.counts?.confirmed || 0, cls: "text-emerald-600" },
          { key: "opened", label: "Aperti", val: stats.counts?.opened || 0, cls: "text-sky-600" },
          { key: "prepared", label: "Preparati", val: stats.counts?.prepared || 0, cls: "text-amber-600" },
          { key: "cancelled", label: "Annullati", val: stats.counts?.cancelled || 0, cls: "text-muted-foreground" },
        ].map((c) => (
          <button key={c.key || "all"} data-testid={`wa-count-${c.key || "all"}`} onClick={() => setStatusFilter(c.key)}
            className={cn("rounded-lg border bg-card p-4 text-left transition-colors hover:border-emerald-400", statusFilter === c.key && "ring-2 ring-emerald-400")}>
            <div className={cn("font-heading text-3xl font-black", c.cls)}>{c.val}</div>
            <div className="text-xs text-muted-foreground mt-0.5">{c.label}</div>
          </button>
        ))}
      </div>
      <p className="text-xs text-muted-foreground -mt-2">
        "Confermato" = conferma manuale del commerciale (NON è prova tecnica di invio/consegna). I messaggi partono manualmente da WhatsApp Web/app.
      </p>

      {isAdmin && stats.by_user?.length > 0 && (
        <div className="rounded-lg border bg-card p-4">
          <div className="text-sm font-heading font-bold mb-2">Confermati per commerciale</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {stats.by_user.map((u) => (
              <div key={u.user_id} data-testid={`wa-byuser-${u.user_id}`} className="flex items-center justify-between rounded-md border px-3 py-2">
                <span className="text-sm truncate">{u.name}</span>
                <span className="font-heading font-black tabular-nums">{u.count}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Cronologia */}
      <div className="rounded-lg border bg-card overflow-hidden">
        <div className="px-4 py-3 border-b font-heading font-bold text-sm">Cronologia messaggi</div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-muted-foreground border-b bg-muted/40">
              <tr>
                <th className="px-4 py-2.5 font-medium">Lead</th>
                <th className="px-3 py-2.5 font-medium hidden md:table-cell">Telefono</th>
                {isAdmin && <th className="px-3 py-2.5 font-medium hidden lg:table-cell">Commerciale</th>}
                <th className="px-3 py-2.5 font-medium">Messaggio</th>
                <th className="px-3 py-2.5 font-medium">Stato</th>
                <th className="px-3 py-2.5 font-medium whitespace-nowrap">Data</th>
                <th className="px-3 py-2.5"></th>
              </tr>
            </thead>
            <tbody data-testid="wa-messages-table">
              {loading ? (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">Caricamento…</td></tr>
              ) : messages.length === 0 ? (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">Nessun messaggio nel periodo</td></tr>
              ) : messages.map((m) => (
                <tr key={m.id} className="border-b last:border-0 hover:bg-accent/40">
                  <td className="px-4 py-2.5">
                    <Link to={`/leads/${m.lead_id}`} className="font-medium hover:underline">{m.lead_name}</Link>
                  </td>
                  <td className="px-3 py-2.5 hidden md:table-cell text-muted-foreground">{m.lead_phone || "—"}</td>
                  {isAdmin && <td className="px-3 py-2.5 hidden lg:table-cell">{m.sender_name || "—"}</td>}
                  <td className="px-3 py-2.5 max-w-[320px] truncate text-muted-foreground" title={m.message}>{m.message || "—"}</td>
                  <td className="px-3 py-2.5">
                    <WaStatusBadge status={m.status} legacy={m.legacy} />
                  </td>
                  <td className="px-3 py-2.5 whitespace-nowrap text-muted-foreground">{fmt(m.created_at)}</td>
                  <td className="px-3 py-2.5">
                    <Link to={`/leads/${m.lead_id}`} className="inline-flex items-center gap-1 text-xs text-emerald-600 hover:underline">
                      Apri <ExternalLink className="w-3 h-3" />
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function WaStatusBadge({ status, legacy }) {
  const map = {
    confirmed: { label: legacy ? "Confermato (non verif.)" : "Confermato", cls: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400" },
    opened: { label: "Aperto", cls: "bg-sky-500/15 text-sky-700 dark:text-sky-400" },
    prepared: { label: "Preparato", cls: "bg-amber-500/15 text-amber-700 dark:text-amber-400" },
    cancelled: { label: "Annullato", cls: "bg-muted text-muted-foreground" },
  };
  const s = map[status] || map.prepared;
  return <span className={cn("inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium", s.cls)}>{s.label}</span>;
}

